import type { Address } from '@gm-commercial/location-model';
import { toLower } from 'lodash-es';
import type { DateTime as DateTimeType } from 'luxon';
import { DateTime } from 'luxon';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';

import {
  DEFAULT_EMPTY_VALUE,
  DEFAULT_SESSION_CONFIG_KEY,
  LanguageLocale,
  LOCALE_FORMAT_TIME,
  LUXON_TIMEZONE_ABBREVIATION_FORMAT,
  LUXON_WEEKDAY_SHORT_FORMAT,
  type PhoneHours,
  TIMEZONE_NEW_YORK,
} from '../constants/common.constant';
import {
  type Entity,
  type TimeZoneOption,
  UserSupportedLocale,
} from '../models/common.model';
import type { BDTableData } from '../models/table/table.model';

export const omitFrom = <
  T extends Partial<Record<string, unknown>>,
  K extends [...(keyof T)[]],
>(
  obj: T,
  ...keys: K
): Omit<T, K[number]> => {
  const withoutOmitted = {} as T;
  for (const k in obj) {
    if (!keys.includes(k)) {
      withoutOmitted[k] = obj[k];
    }
  }
  return { ...withoutOmitted };
};

export const pickFrom = <T, K extends [...(keyof T)[]]>(
  obj: T,
  ...keys: K
): Pick<T, K[number]> => {
  return keys.reduce(
    (picked, key) => ({ ...picked, [key]: obj[key] }),
    {} as Pick<T, K[number]>
  );
};

export const pickEntityProps = <T extends Entity>(
  obj: T
): Pick<T, keyof Entity> => {
  return pickFrom(obj, 'id', 'name');
};

export const removeUndefined = <T extends Partial<Record<string, unknown>>>(
  obj: T
): Partial<Record<string, unknown>> => {
  const withoutOmitted = {} as T;
  for (const k in obj) {
    if (obj[k] !== undefined) {
      withoutOmitted[k] = obj[k];
    }
  }
  return { ...withoutOmitted };
};
export const removeEmpty = <T extends Partial<Record<string, unknown>>>(
  obj: T
): Partial<Record<string, unknown>> => {
  const withoutEmpty = {} as T;
  for (const k in obj) {
    if (obj[k] || typeof obj[k] === 'number' || typeof obj[k] === 'boolean') {
      withoutEmpty[k] = obj[k];
    }
  }
  return { ...withoutEmpty };
};

export const capitalize = (
  str?: string,
  defaultEmptyValue?: string
): string => {
  return str
    ? str.length > 1
      ? str.charAt(0).toLocaleUpperCase() + str.slice(1).toLocaleLowerCase()
      : str.toLocaleUpperCase()
    : defaultEmptyValue || DEFAULT_EMPTY_VALUE;
};

export const toSentenceCase = (str?: string): string => {
  if (!str || str.trim() === '') {
    return DEFAULT_EMPTY_VALUE;
  }
  return toLower(str).replace(/^./, (match) => match.toUpperCase());
};

export const uppercaseLiteral = <T extends string>(
  s?: T
): `${Uppercase<T>}` => {
  return s?.toUpperCase() as `${Uppercase<T>}`;
};

export const lowercaseLiteral = <T extends string>(
  s?: T
): `${Lowercase<T>}` => {
  return s?.toLocaleLowerCase() as `${Lowercase<T>}`;
};

export const useDefault = <T>(input: T, defaultValue: T): T => {
  return (typeof input === 'number' && (input as number) === 0) ||
    (typeof input === 'boolean' && (input as boolean) === false) ||
    input
    ? input
    : defaultValue;
};

export const getDisplayKeys = <T extends Record<string, unknown>>(
  e: T
): Array<keyof T> => {
  return Object.keys(e).filter((k) => Number.isNaN(Number(k)));
};

export const isObject = (
  value: unknown
): value is Record<string | number | symbol, unknown> =>
  typeof value === 'object' && value !== null;

export const notEmpty = <T>(
  value: T
): value is Exclude<T, undefined | null> => {
  if (Array.isArray(value) || typeof value === 'string')
    return value.length !== 0;
  if (isObject(value)) return Object.keys(value).length !== 0;
  return !(value === null || value === undefined);
};

export const getPercentage = (total: number, value: number): number =>
  total ? Math.round((value * 100) / total) : 0;

export const getRandomEnumValue = (enumeration: { [x: string]: unknown }) => {
  const values = getDisplayKeys(enumeration);
  const enumKey = values[Math.floor(Math.random() * values.length)];
  return enumeration[enumKey];
};

/**
 * A number as a fixed-point string.
 *
 * @deprecated Use the measurement components, or `useFormat().<kind>.digits()` when the digit count
 * itself is needed. The core's precision policy decides digits from the quantity and the unit, where
 * this takes the count from its caller — which is how the same measurement ends up with a different
 * number of decimals on two screens. Note also that the decimal separator here is always a point,
 * so a French reader sees `1.5` where they write `1,5`.
 */
export const getRoundedValue = (
  value: number,
  decimalPlaces: number
): string => (value ? value.toFixed(decimalPlaces) : '0');

export const countArrayAttributeValues = (obj?: object) =>
  obj
    ? Object.values(obj).reduce(
        (count, attribute) => count + (attribute?.length || 0),
        0
      )
    : 0;

export const filterByPermanentKeys = (obj?: object, permanentKeys = []) =>
  obj
    ? Object.entries(obj)
        ?.filter(([name]) => !permanentKeys?.find((value) => value === name))
        .reduce((accumulator, [name, value]) => {
          return { ...accumulator, [name]: value };
        }, {})
    : undefined;

export const getTimezone = (
  value: string,
  locale: string = LanguageLocale.EN
): string => {
  const timeZoneName = Intl.DateTimeFormat(locale, {
    timeZone: value,
    timeZoneName: 'long',
  }).formatToParts(new Date());
  const currentDate = DateTime.now();
  const offsetValue = DateTime.fromObject(
    {
      year: currentDate.year,
      month: currentDate.month,
      day: currentDate.day,
    },
    {
      zone: value,
    }
  ).toFormat('ZZ');
  const timeZoneLabelValue = value.replace(/[^a-zA-Z ]/g, ' ');
  return value
    ? ((`(GMT${offsetValue})` +
        ' ' +
        timeZoneName.find((part) => part.type === 'timeZoneName')
          ?.value) as string) +
        ' - ' +
        timeZoneLabelValue
    : DEFAULT_EMPTY_VALUE;
};
export const getAllTimezones = () => {
  let timeZones: string[] | undefined;
  let updatedTimeZones = [] as TimeZoneOption[];
  const intlWithSupportedValues = Intl as typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[];
  };
  if (intlWithSupportedValues.supportedValuesOf) {
    timeZones = intlWithSupportedValues.supportedValuesOf('timeZone');
    updatedTimeZones = timeZones.map((timeZone) => ({
      value: timeZone,
      label: getTimezone(timeZone),
    }));
    updatedTimeZones.sort((a, b) => a.label.localeCompare(b.label));

    return updatedTimeZones;
  }
  return updatedTimeZones;
};

const hasDisplayAddress = (
  addr: unknown
): addr is { displayAddress: string } => {
  return (addr as { displayAddress: string }).displayAddress !== undefined;
};

/**
 * An address on one line.
 *
 * @deprecated Use `<Address>` from `@brightdrop/localization-core`, or `useFormat().address`,
 * mapping these fields onto `AddressFields`. The layout follows the address's country when the
 * core recognises it, and the reader's own style when it does not. A `displayAddress` already on
 * the value is returned unchanged; the comma-joined US order below applies only when there is none.
 */
export const getFullAddress = (addr: Address | BDTableData): string => {
  if (addr && typeof addr !== 'string') {
    if (hasDisplayAddress(addr)) {
      return addr.displayAddress;
    }
    const result = [
      addr.address1,
      addr.address2 || '',
      addr.city,
      addr.stateProvinceTerritory,
      addr.postalCode,
    ]
      .filter((part) => !!part && part !== ' ')
      .join(', ')
      .trim();

    return result || DEFAULT_EMPTY_VALUE;
  }
  return addr || DEFAULT_EMPTY_VALUE;
};

export const isUnsafeObjectKey = (key: string) => {
  const unsafeKeys = ['__proto__', 'constructor', 'prototype'];

  if (unsafeKeys.includes(key)) {
    console.error('Unsafe object key detected');
    return true;
  }

  return false;
};

/**
 * Creates a safe session ID by validating it against unsafe object keys
 * and ensuring it matches expected patterns to prevent prototype pollution attacks
 * @param sessionId - The original session ID to validate
 * @param fallback - The fallback value to use if sessionId is unsafe (default: DEFAULT_SESSION_CONFIG_KEY)
 * @returns A safe session ID string
 */
export const createSafeSessionId = (
  sessionId: string | undefined | null,
  fallback: string = DEFAULT_SESSION_CONFIG_KEY
): string => {
  // Return fallback for null/undefined/empty values
  if (!sessionId) {
    return fallback;
  }

  // Check for unsafe object keys that could cause prototype pollution
  if (isUnsafeObjectKey(sessionId)) {
    return fallback;
  }

  // Additional validation: ensure sessionId contains only safe characters
  // Allow alphanumeric characters, hyphens, underscores, and dots
  // This prevents any potential injection of special characters
  const safePattern = /^[a-zA-Z0-9._-]+$/;
  if (!safePattern.test(sessionId)) {
    return fallback;
  }

  return String(sessionId);
};

export const getNonNullValueOrEmptyString = (
  value: string | number | undefined
): string => {
  return value !== undefined && value !== null ? String(value) : '';
};

export function extractLastChars(
  value: string | null | undefined,
  count: number
): string {
  if (!value || count <= 0) return '';
  const str = value.toString().trim();
  return str.length > count ? str.slice(-count) : str;
}

export const calculatePercentage = (total: number, value: number): string => {
  const percentage = getPercentage(total, value);
  return `${percentage}%`;
};

export const convertPhoneHoursToDateTime = (
  phoneHours: PhoneHours
): { startDateTime: DateTimeType; endDateTime: DateTimeType } => {
  const startDateTime = DateTime.now()
    .setZone(phoneHours.timezone)
    .startOf('week')
    .plus({ days: phoneHours.startDay })
    .set({
      hour: phoneHours.startHour,
      minute: phoneHours.startMinute ?? 0,
    });
  const endDateTime = DateTime.now()
    .setZone(phoneHours.timezone)
    .startOf('week')
    .plus({ days: phoneHours.endDay })
    .set({
      hour: phoneHours.endHour,
      minute: phoneHours.endMinute ?? 0,
    });
  return { startDateTime, endDateTime };
};

/**
 * Formats time range display string with localized times and day abbreviations.
 * Uses Luxon for locale-appropriate time (including minutes) and weekday strings.
 * @param startDateTime - Start Luxon DateTime object (mandatory)
 * @param endDateTime - End Luxon DateTime object (mandatory)
 * @param timezone - Target timezone for display (optional, defaults to EST)
 * @param locale - Locale for 12/24 hour and weekday formatting (optional)
 * @param translationKey - Translation key for the format (optional)
 * @returns Formatted string like "Mon–Sat, 8:00 AM–9:00 PM EST"
 */
export const formatLocalizedTimeRange = (
  startDateTime: DateTimeType,
  endDateTime: DateTimeType,
  timezone: string = TIMEZONE_NEW_YORK,
  locale?: string,
  translationKey?: string
): string => {
  const effectiveLocale =
    locale && locale in LOCALE_FORMAT_TIME
      ? locale
      : (UserSupportedLocale.en_US as keyof typeof LOCALE_FORMAT_TIME);

  const localStart = startDateTime.setZone(timezone).setLocale(effectiveLocale);
  const localEnd = endDateTime.setZone(timezone).setLocale(effectiveLocale);

  let startTime = localStart.toLocaleString(DateTime.TIME_SIMPLE);
  let endTime = localEnd.toLocaleString(DateTime.TIME_SIMPLE);
  const startDay = localStart.toFormat(LUXON_WEEKDAY_SHORT_FORMAT);
  const endDay = localEnd.toFormat(LUXON_WEEKDAY_SHORT_FORMAT);
  const timezoneAbbr = localStart.toFormat(LUXON_TIMEZONE_ABBREVIATION_FORMAT);

  const is12HourOutput = /(AM|PM|a\.m\.|p\.m\.)/i.test(startTime);

  if (is12HourOutput && localStart.minute === 0 && localEnd.minute === 0) {
    const stripZeroMinutes = (s: string) =>
      s.replace(/:00(?=\s*(AM|PM|a\.m\.|p\.m\.))/i, '');
    startTime = stripZeroMinutes(startTime);
    endTime = stripZeroMinutes(endTime);
  }

  const formatKey =
    translationKey ??
    (is12HourOutput
      ? 'common:phoneHours.format12Hour'
      : 'common:phoneHours.format24Hour');

  return translateText(formatKey, {
    startDay,
    endDay,
    startTime,
    endTime,
    timezone: timezoneAbbr,
  });
};
